/**
 * The plot itself: one window of response times, drawn by recharts.
 *
 * Only checks the site answered carry a value. A failed check is a break in
 * the line rather than a zero or a spike: a timeout has no response time,
 * and drawing it as ten seconds would make a slow server and a dead one the
 * same shape. Where the site was down the window is shaded red behind the
 * line (a down span is at least one check interval wide, or a single failure
 * would be a hairline), so the eye reads "it stopped here" rather than "it
 * got fast".
 *
 * Buckets add a faint band from the fastest answer to the p95 behind their
 * median; a raw check has no spread, so a raw window is the line over a
 * light fill. A point with no answered neighbour on either side gets a dot,
 * or a lone answer between two failures would not be drawn at all.
 *
 * The y axis stops short of a lone spike (`yScale` in axis.ts), so the line
 * can run above its top. Where it does, it is clipped at the edge and marked
 * there with a small triangle: the reader sees that something went higher and
 * hovers it for the figure. While the window follows now, the newest answer wears a dot.
 *
 * The x domain is exactly the window, with overflow allowed, so the
 * neighbours `viewSeries` adds on each side draw the line out to the frame's
 * edge and are clipped there. The measure is time to first byte, how long
 * the server took to start answering, not page speed; the card's subtitle
 * says so.
 */
import { memo, useMemo } from "react";
import { Area, CartesianGrid, ComposedChart, ReferenceArea, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@wa/ui";
import { MINUTE_MS, formatMs, tickShape, type ChartRow, type MonitorDownSpan } from "../../../../lib/monitoring";
import type { T } from "../words";
import { timeTicks, type YScale } from "./axis";
import { tickText } from "./labels";
import { PLOT_LEFT, PLOT_RIGHT, PLOT_TOP, X_AXIS_HEIGHT, X_TICK_MARGIN } from "./layout";
import { SeriesTooltip } from "./SeriesTooltip";

interface DotProps {
  cx?: number;
  cy?: number;
  index?: number;
  key?: string;
}

export const ResponsePlot = memo(function ResponsePlot({
  rows,
  downSpans,
  from,
  span,
  step,
  checkMinutes,
  plotWidth,
  y,
  pinned,
  t,
}: {
  rows: ChartRow[];
  downSpans: MonitorDownSpan[];
  from: number;
  span: number;
  step: number;
  checkMinutes: number;
  plotWidth: number;
  y: YScale;
  /** The window follows now: the newest answer gets a dot. */
  pinned: boolean;
  t: T;
}) {
  const to = from + span;
  const shape = tickShape(span);
  const ticks = useMemo(() => timeTicks(from, to, plotWidth), [from, to, plotWidth]);
  const config = useMemo(
    () => ({ p50: { label: t("monitoring.chart.series"), color: "var(--color-primary)" } }) satisfies ChartConfig,
    [t],
  );
  const minDownMs = Math.max(1, checkMinutes) * MINUTE_MS;
  // The newest answer on screen, marked while the chart follows now.
  const liveIndex = useMemo(() => {
    if (!pinned) return -1;
    for (let i = rows.length - 1; i >= 0; i--) if (rows[i]!.p50 !== null && rows[i]!.x <= to) return i;
    return -1;
  }, [rows, pinned, to]);

  const dot = (props: DotProps) => {
    const i = props.index ?? -1;
    const row = rows[i];
    if (!row || row.p50 === null || props.cx === undefined || row.x < from || row.x > to) return <g key={props.key} />;
    const lone = rows[i - 1]?.p50 == null && rows[i + 1]?.p50 == null;
    // The line cut off at the axis top: the marker says it went higher. A band cut off
    // there is not marked; it already runs into the edge, and a zoomed-out window has a
    // slow check in many buckets, so marking it set a row of triangles along the top.
    const clipped = row.p50 > y.top;
    const cx = props.cx;
    return (
      <g key={props.key}>
        {lone && !clipped ? <circle cx={cx} cy={props.cy} r={2.25} fill="var(--color-p50)" /> : null}
        {clipped ? (
          <path
            d={`M${cx - 4},${PLOT_TOP + 6}L${cx + 4},${PLOT_TOP + 6}L${cx},${PLOT_TOP}Z`}
            fill="var(--color-p50)"
            stroke="var(--crm-surface)"
            strokeWidth={1.5}
          />
        ) : null}
        {i === liveIndex && !clipped ? (
          <circle cx={cx} cy={props.cy} r={4} fill="var(--color-p50)" stroke="var(--crm-surface)" strokeWidth={2} />
        ) : null}
      </g>
    );
  };

  return (
    <ChartContainer config={config} className="aspect-auto h-[180px] w-full sm:h-[220px]">
      <ComposedChart data={rows} margin={{ top: PLOT_TOP, right: PLOT_RIGHT, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="x"
          type="number"
          domain={[from, to]}
          allowDataOverflow
          ticks={ticks}
          interval={0}
          tickFormatter={(v: number) => tickText(v, shape)}
          tickLine={false}
          axisLine={false}
          tickMargin={X_TICK_MARGIN}
          height={X_AXIS_HEIGHT}
        />
        <YAxis
          width={PLOT_LEFT}
          domain={[0, y.top]}
          ticks={y.ticks}
          interval={0}
          allowDataOverflow
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => formatMs(v)}
        />
        {downSpans.map((s) => (
          <ReferenceArea
            key={s.from}
            x1={s.from}
            x2={Math.max(s.to, s.from + minDownMs)}
            fill="var(--color-destructive)"
            fillOpacity={0.14}
            strokeOpacity={0}
            ifOverflow="hidden"
          />
        ))}
        <ChartTooltip
          cursor={{ strokeWidth: 1 }}
          isAnimationActive={false}
          // Above the edge fades (z 1, `EdgeFade`), which share its stacking context.
          wrapperStyle={{ zIndex: 2 }}
          // A failed check's value is null; filtered out, its tooltip (and the reason it failed) would never show.
          filterNull={false}
          content={({ active, payload }) => (
            <SeriesTooltip
              active={Boolean(active)}
              row={(payload?.[0]?.payload as ChartRow | undefined) ?? null}
              step={step}
              withDay={shape !== "time"}
              t={t}
            />
          )}
        />
        {step > 0 ? (
          <Area
            dataKey="band"
            type="monotoneX"
            stroke="none"
            fill="var(--color-p50)"
            fillOpacity={0.14}
            connectNulls={false}
            isAnimationActive={false}
            activeDot={false}
          />
        ) : null}
        <Area
          dataKey="p50"
          type="monotoneX"
          stroke="var(--color-p50)"
          strokeWidth={2}
          fill="var(--color-p50)"
          fillOpacity={step > 0 ? 0 : 0.1}
          connectNulls={false}
          isAnimationActive={false}
          dot={dot}
          activeDot={{ r: 3.5 }}
        />
      </ComposedChart>
    </ChartContainer>
  );
});
